/**
 * US4/T021 (FR-013, FR-028, DA-07). File-picker step of a two-route validate → preview → commit
 * flow — the same shape `001`'s catalogue importer spec calls for (`/imports` → `/imports/[batchId]`,
 * screen-routes.md FR-027). `Preview.tsx` is the other half.
 *
 * There is no `ImportWizard` to promote (research R2, tasks.md T006): nothing under
 * `apps/admin/components` or `packages/theme` exists by that name, and `001`'s own catalogue
 * importer is not built in this app's current Vite incarnation either (`lib/nav.tsx`'s `/imports`
 * entry is `built: false`) — so there is no second consumer yet, and nothing to share a component
 * with. T006 stays open; this is a self-contained, one-consumer screen, composing the template's
 * raw dropzone primitive directly
 * (vendor/isomorphic/apps/isomorphic/src/app/shared/file-upload.tsx's own `Upload` usage) rather
 * than that file's `FileInput` wrapper — its submit handler is a `console.log`/toast stub wired to
 * nothing (read, not assumed), so writing a real `onChange` here is composition, not a rebuild.
 *
 * The file goes to Files first (016), which enforces the size and type; validation gets its hash.
 */
import { useNavigate, useOutletContext } from "react-router-dom";
import { Text } from "rizzui";
import { PageHeader, ErrorSurface } from "@steelyard/theme";
import Upload from "@core/ui/upload";
import { can } from "../../../lib/permissions";
import type { AccountSummary, PermissionState } from "../../../types";
import { validateStockUpload } from "../../../lib/services/warehouse";
import { cacheUpload } from "./upload-cache";
import { UPLOAD_PREVIEW_PATH } from "../../../lib/paths";
import { UploadDropHint, UploadBusyHint } from "../../../components/upload-drop-hint";
import { useFileUpload } from "@steelyard/common";

export default function StockUploadList() {
  const navigate = useNavigate();
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const upload = useFileUpload("stock-upload", {
    onUploaded: async (uploaded, file) => {
      cacheUpload(await validateStockUpload(file.name, uploaded.hash));
      navigate(UPLOAD_PREVIEW_PATH);
    },
  });
  const isSubmitting = upload.busy;

  if (!can(permissionState, "inventory.administer")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="upload stock" />;
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
      <PageHeader
        title="Bulk Stock Upload"
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Stock", href: "/inventory" }, { name: "Bulk Upload" }]}
      />

      <Text className="text-gray-500">
        CSV, one <span className="font-mono">skuCode,quantity</span> row per line: the SKU code, and the
        quantity to add in the SKU&apos;s stocking unit. Further columns are ignored. Up to 10&nbsp;MB. Nothing is written to stock until you review the
        preview and commit it.
      </Text>

      {upload.error ? <ErrorSurface error={upload.error} action="validate this file" /> : null}

      <Upload
        accept="csv"
        multiple={false}
        disabled={isSubmitting}
        onChange={upload.onChange}
        aria-label="Stock upload CSV file"
        placeholderText={isSubmitting ? <UploadBusyHint label="Validating…" /> : <UploadDropHint />}
      />
    </div>
  );
}
